import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { CoreService } from '../../services'
import { SchemaUtil } from '../../utils'
import { paymentsColumns } from '../../utils/definitions.util'
import { PaymentService } from '../../services/entity-services/payment.service'
import { injectInfiniteQuery } from '@tanstack/angular-query-experimental'

const DEFAULT_FILTERS = {
  name: '',
}

@Component({
  selector: 'app-payments',
  templateUrl: 'payments.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PaymentsComponent extends BaseListing implements OnInit {
  private paymentService = inject(PaymentService)

  listingColumns = signal(paymentsColumns)

  sortProp = signal('name')
  sortDir = signal('asc')

  filters = signal(structuredClone(DEFAULT_FILTERS))

  canCreate = computed(() => {
    return (
      this.core.services.user.hasRole('ROLE_PAYMENT_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    )
  })

  readonly queryFilters = computed(() => {
    const value = this.filters()
    const f: Record<string, any> = {}
    if (value.name?.length) f['name'] = value.name
    return f
  })

  readonly queryPayments = injectInfiniteQuery(() => ({
    queryKey: ['payments', { sortProp: this.sortProp(), sortDir: this.sortDir() }],
    queryFn: async ({ pageParam }) => {
      // Persist current view settings before fetching
      return await this.paymentService
        .getPayments(
          {
            page: pageParam,
            sort: this.sortProp(),
            direction: this.sortDir(),
            filters: this.queryFilters(),
          },
          SchemaUtil.payment
        )
        .then(result => {
          this.saveViewSettings('payments', {
            filters: this.filters(),
            sortProp: this.sortProp(),
            sortDir: this.sortDir(),
          })
          return result
        })
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
  }))

  totalItems = computed(() => this.queryPayments.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  constructor(protected core: CoreService) {
    super(core)
    effect(() => {
      this.queryPayments.data()
    })
  }

  ngOnInit() {
    const view = this.loadViewSettings('payments')
    this.filters.set(view?.filters || structuredClone(DEFAULT_FILTERS))
    this.sortProp.set(view?.sortProp || 'name')
    this.sortDir.set(view?.sortDir || 'asc')
    this.loadSorting('payments')
  }

  onNameFilterChange(name: string) {
    this.filters.update(v => ({ ...v, name: name ?? '' }))
  }

  clearFilters(): void {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.queryPayments.refetch()
  }
}
